import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Zap, 
  Users, 
  Trophy, 
  Play,
  Star,
  Shield,
  Crown,
  Sparkles
} from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { getBreadcrumbSchema, getFAQSchema, getHowToSchema, getGameSchema } from "@/utils/structuredData";
import { supabase } from "@/integrations/supabase/client";

const ComoJogarPokemonTCG = () => {
  const navigate = useNavigate();
  const [mentorCount, setMentorCount] = useState(0);

  // Buscar número de mentores de Pokemon TCG
  useEffect(() => {
    const fetchMentorCount = async () => {
      try {
        const { data: gameData } = await supabase
          .from("games")
          .select("id")
          .ilike("name", "%pokemon%")
          .single();

        if (gameData) {
          const { data: offerings } = await supabase
            .from("mentorship_offerings")
            .select("mentor_id")
            .eq("game_id", gameData.id)
            .eq("is_active", true);

          const uniqueMentors = new Set(offerings?.map(o => o.mentor_id) || []);
          setMentorCount(uniqueMentors.size);
        }
      } catch (error) {
        console.error("Erro ao buscar mentores:", error);
      }
    };

    fetchMentorCount();
  }, []);

  // FAQ para dados estruturados
  const faqs = [
    {
      question: "Como começar a jogar Pokemon TCG?",
      answer: "Para começar no Pokemon TCG, compre um deck inicial, aprenda as regras básicas, pratique com amigos e participe de torneios locais. É importante entender os tipos de cartas e como construir um deck balanceado."
    },
    {
      question: "Qual é o melhor deck para iniciantes?",
      answer: "Decks temáticos são ideais para iniciantes, especialmente os que focam em um tipo de Pokemon. Decks de Pikachu, Charizard ou Lucario são boas opções para aprender o básico."
    },
    {
      question: "Como construir um deck competitivo?",
      answer: "Um deck competitivo precisa de 60 cartas balanceadas: 10-15 Pokemon, 25-35 cartas de Treinador e 10-15 cartas de Energia. Foque em sinergias e tenha cartas de draw e busca."
    },
    {
      question: "Vale a pena ter um mentor de Pokemon TCG?",
      answer: "Sim! Um mentor acelera seu aprendizado sobre meta, construção de decks, estratégias avançadas e te prepara para torneios competitivos."
    }
  ];

  // Passos para dados estruturados HowTo
  const howToSteps = [
    {
      name: "Comprar Deck Inicial",
      text: "Adquira um deck temático ou starter deck para aprender as regras básicas do jogo."
    },
    {
      name: "Aprender as Regras",
      text: "Estude o manual oficial e assista tutoriais para entender mecânicas, tipos e evoluções."
    },
    {
      name: "Praticar Jogos",
      text: "Jogue contra amigos ou online no Pokemon TCG Online para ganhar experiência."
    },
    {
      name: "Construir Deck",
      text: "Monte seu próprio deck de 60 cartas balanceando Pokemon, Treinadores e Energias."
    }
  ];

  // SEO otimizado
  useSEO({
    title: "Como Jogar Pokemon TCG - Guia Completo 2024 para Iniciantes",
    description: "Aprenda como jogar Pokemon TCG do zero! Guia completo com regras, melhores decks, estratégias e como competir. Mentores profissionais disponíveis.",
    keywords: "como jogar pokemon tcg, pokemon tcg iniciantes, guia pokemon tcg, decks pokemon, mentor pokemon tcg, coach pokemon tcg, cartas pokemon, torneios pokemon",
    canonicalUrl: "https://arena-pro-player.com/como-jogar-pokemon-tcg",
    structuredData: [
      getBreadcrumbSchema([
        { name: "Início", url: "https://arena-pro-player.com/" },
        { name: "Jogos", url: "https://arena-pro-player.com/jogos" },
        { name: "Como Jogar Pokemon TCG", url: "https://arena-pro-player.com/como-jogar-pokemon-tcg" }
      ]),
      getFAQSchema(faqs),
      getHowToSchema("Como Jogar Pokemon TCG", "Guia completo para iniciantes aprenderem Pokemon TCG", howToSteps),
      getGameSchema({ name: "Pokemon TCG", cover_url: "/pokemon-tcg-cover.jpg" }, mentorCount)
    ]
  });

  return (
    <div className="min-h-screen bg-[#0f141c] text-white">
      {/* Hero Section */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-[#0f141c] via-[#1a2332] to-[#0f141c]">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-center opacity-5"></div>
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Badge className="mb-4 bg-[#FFCB05] text-black">
              <Zap className="h-4 w-4 mr-2" />
              Pokemon TCG
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Como Jogar <span className="text-[#FFCB05]">Pokemon TCG</span>
              <br />
              <span className="text-2xl md:text-3xl text-gray-300">Guia Completo 2024</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Aprenda Pokemon TCG do zero com nosso guia completo! Regras, melhores decks, 
              estratégias competitivas e como dominar torneios com mentores profissionais.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-[#FFCB05] hover:bg-[#E6B800] text-black"
                onClick={() => navigate("/mentor-pokemon-tcg")}
              >
                <Users className="h-5 w-5 mr-2" />
                Encontrar Mentor de Pokemon TCG
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-[#FFCB05] text-[#FFCB05] hover:bg-[#FFCB05] hover:text-black"
                onClick={() => navigate("/mentorias?game=pokemon-tcg")}
              >
                <Play className="h-5 w-5 mr-2" />
                Ver Mentorias
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* O que é Pokemon TCG */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              O que é <span className="text-[#FFCB05]">Pokemon TCG</span>?
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              Pokemon Trading Card Game é um jogo de cartas colecionáveis estratégico onde 
              você treina Pokemon para batalhar contra outros treinadores. Combine estratégia, 
              sorte e conhecimento para se tornar um mestre Pokemon.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="bg-[#181D24] border-[#222A35]">
              <CardHeader>
                <Sparkles className="h-8 w-8 text-[#FFCB05] mb-2" />
                <CardTitle className="text-white">Estratégia Profunda</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300">
                  Cada partida exige planejamento, gestão de recursos e 
                  tomada de decisões estratégicas complexas.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#181D24] border-[#222A35]">
              <CardHeader>
                <Crown className="h-8 w-8 text-[#FFCB05] mb-2" />
                <CardTitle className="text-white">Colecionável</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300">
                  Além de jogar, você coleciona cartas raras e valiosas, 
                  construindo sua própria coleção única.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#181D24] border-[#222A35]">
              <CardHeader>
                <Users className="h-8 w-8 text-[#FFCB05] mb-2" />
                <CardTitle className="text-white">Comunidade Global</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300">
                  Participe de torneios mundiais e conecte-se com 
                  milhões de jogadores ao redor do mundo.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-[#181D24]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Perguntas <span className="text-[#FFCB05]">Frequentes</span>
            </h2>
            <p className="text-xl text-gray-300">
              Respostas para as dúvidas mais comuns sobre Pokemon TCG.
            </p>
          </motion.div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-[#0f141c] border-[#222A35]">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300">{faq.answer}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#FFCB05] to-[#E6B800]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              Pronto para se Tornar um Mestre Pokemon?
            </h2>
            <p className="text-xl text-black/80 mb-8">
              Acelere seu progresso com mentores profissionais. Aprenda estratégias avançadas, 
              construção de decks e domine torneios competitivos.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-black text-[#FFCB05] hover:bg-gray-800"
                onClick={() => navigate("/mentor-pokemon-tcg")}
              >
                <Users className="h-5 w-5 mr-2" />
                Encontrar Mentor Agora
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-black text-black hover:bg-black hover:text-[#FFCB05]"
                onClick={() => navigate("/mentorias?game=pokemon-tcg")}
              >
                <Trophy className="h-5 w-5 mr-2" />
                Ver Todas as Mentorias
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ComoJogarPokemonTCG; 